import { useState, useRef, useEffect } from "react";
import { MessageCircle, X, Send } from "lucide-react";
import api from "@/lib/api";

const sid = (() => {
  let s = localStorage.getItem("cc_assist_sid");
  if (!s) { s = Math.random().toString(36).slice(2) + Date.now().toString(36); localStorage.setItem("cc_assist_sid", s); }
  return s;
})();

export function AssistantWidget() {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([{ role: "assistant", text: "Hi! How can I help today?" }]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  useEffect(() => { if (open) endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setMsgs((m) => [...m, { role: "user", text }]);
    setBusy(true);
    try {
      const { data } = await api.post("/assistant/chat", { session_id: sid, message: text });
      setMsgs((m) => [...m, { role: "assistant", text: data.reply }]);
    } catch (e) {
      setMsgs((m) => [...m, { role: "assistant", text: "Sorry — I couldn't reach the assistant just now. Please try again." }]);
    } finally { setBusy(false); }
  };

  return (
    <>
      {!open && (
        <button data-testid="assistant-open" onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-[#1F3C88] text-white shadow-xl flex items-center justify-center hover:bg-[#F7941D] transition-colors duration-200">
          <MessageCircle className="w-6 h-6" />
        </button>
      )}
      {open && (
        <div data-testid="assistant-panel" className="fixed bottom-6 right-6 z-50 w-[92vw] max-w-sm h-[70vh] max-h-[560px] bg-white border border-[#E5E5E2] rounded-2xl shadow-2xl flex flex-col overflow-hidden">
          <div className="bg-[#1F3C88] text-white px-4 py-3 flex items-center justify-between">
            <div>
              <div className="text-xs tracking-[0.15em] uppercase text-[#F7941D] font-semibold">Command Center</div>
              <div className="text-sm font-semibold">Assistant</div>
            </div>
            <button data-testid="assistant-close" onClick={() => setOpen(false)} className="hover:text-[#F7941D]"><X className="w-5 h-5" /></button>
          </div>
          <div className="flex-1 overflow-y-auto p-3 space-y-3 bg-[#FBFAF8]">
            {msgs.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm whitespace-pre-wrap ${m.role === "user" ? "bg-[#1F3C88] text-white" : "bg-white border border-[#E5E5E2] text-[#0A0A0A]"}`}>{m.text}</div>
              </div>
            ))}
            {busy && <div className="text-xs text-[#8a8a86] pl-1">Thinking…</div>}
            <div ref={endRef} />
          </div>
          <div className="p-3 border-t border-[#E5E5E2] flex items-center gap-2">
            <input data-testid="assistant-input" value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Ask how to…" className="flex-1 px-3 py-2 border border-[#E5E5E2] rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-[#F7941D]" />
            <button data-testid="assistant-send" onClick={send} disabled={busy} className="w-10 h-10 rounded-full bg-[#1F3C88] text-white flex items-center justify-center hover:bg-[#F7941D] transition-colors duration-200 disabled:opacity-60"><Send className="w-4 h-4" /></button>
          </div>
        </div>
      )}
    </>
  );
}
